import axios from 'axios';
import { getStoreLocation } from 'ck-utils';

/**
 * Returns Klaviyo master list ID, defaulting to AU
 * @returns {string}
 */
const getKlaviyoEmailListId = () => {
  const ids = {
    AU: 'TckcSB',
    NZ: 'RBG5WG',
    US: 'XX74wG',
  };
  return ids[getStoreLocation()] || ids.AU;
};

/**
 * Returns Klaviyo SMS Subscribers list id, defaulting to AU
 * @returns {string}
 */
const getKlaviyoSMSListId = () => {
  const ids = {
    AU: 'WipfSL',
    NZ: 'Y4uNjW',
    US: 'Uvju9v',
  };
  return ids[getStoreLocation()] || ids.AU;
};

/**
 * @param {string} email
 * @param {object} preferences
 * @param {string} phone
 * @param {firstName} firstName
 * @param {lastName} lastName
 * @param {string} dob
 * @param {string} source - registration source
 * @param {object} rest - other custom attribute to be sent to Klaviyo
 * @param listId - Klaviyo list ID
 */
const generateSubscriptionData = ({
  source,
  email,
  phone,
  preferences,
  firstName,
  lastName,
  listId,
  ...rest
}) => ({
  data: {
    type: 'subscription',
    attributes: {
      custom_source: source,
      profile: {
        data: {
          type: 'profile',
          attributes: {
            email,
            phone_number: phone,
            first_name: firstName,
            last_name: lastName,
            properties: {
              $consent_method: 'CK Signup Form',
              $source: source,
              'Product Preferences': preferences ? JSON.stringify(Object.keys(preferences).filter(key => preferences[key])) : '',
              ...rest
            }
          }
        }
      }
    },
    relationships: { list: { data: { type: 'list', id: listId } } }
  }
});
/**
 * Post to Klaviyo and SmsBUMP API to sign up
 * @param {string} email
 * @param {object} preferences
 * @param {string} phone
 * @param {string} country
 * @param {string} dob - in format `DD/MM/YYYY`
 * @param {string} source - registration source
 * @param {function} onSuccess
 * @param {function} onFinal
 * @param {boolean} noSMS
 * @param {boolean} noEmail
 * @param {function} trackEvent
 * @param {string} firstName
 * @param {string} lastName
 * @param {object} rest - other custom attribute to be sent to Klaviyo
 */
export const klaviyoSignUp = ({
  email,
  phone,
  preferences,
  dob,
  source,
  onSuccess,
  onFinal,
  noSMS,
  noEmail,
  trackEvent,
  firstName,
  lastName,
  ...rest
}) => {
  const emailListId = getKlaviyoEmailListId();
  const smsListId = getKlaviyoSMSListId();
  const url = `https://a.klaviyo.com/client/subscriptions/?company_id=${process.env.REACT_APP_KLAVIYO_KEY}`;
  const emailListData = generateSubscriptionData(
    { email, preferences, source, firstName, lastName, listId: emailListId, ...rest }
  );

  const smsListData = generateSubscriptionData(
    { phone, preferences, source, firstName, lastName, listId: smsListId, ...rest }
  );

  window.klaviyo?.identify({
    $email: email,
    $phone_number: phone,
  }, () => {
    Promise
      .all([
        noEmail ? null : axios.post(url, emailListData, {
          headers: { revision: '2023-12-15', 'content-type': 'application/json' }
        }),
        noSMS || !phone ? null : axios.post(url, smsListData, {
          headers: { revision: '2023-12-15', 'content-type': 'application/json' }
        })
      ])
      .catch(() => {
        window.CultureKings.Notifications({
          action: 'notice',
          message: 'Error! There was an issue signing up for the newsletter, please try again.'
        });
      })
      .then((response) => {
        onSuccess?.(response);
        window.dataLayer.push({
          event: 'signUpSuccessful'
        });
        if (email) {
          if (typeof trackEvent === 'function') {
            trackEvent();
          } else {
            window._learnq.push(['track', 'Sign-up Form Submitted - Web', {
              source
            }]);
          }
        }
      })
      .finally(onFinal);
  });
};
